import * as Sentry from '@sentry/react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { useLocales } from 'src/locales';
import { useAuthContext } from 'src/auth/hooks';
import { LoadingScreen } from 'src/components/loading-screen';

import { useActingClient } from './acting-client-context';

type Props = { children: React.ReactNode };

/**
 * NAT-2497 — the application-level gate (spec §6).
 *
 * Mounted ABOVE page content and above the checkout provider's fetches, so
 * that a customer with no active venue, or with several and none chosen, sees
 * an explained state and issues no client-scoped request at all. A page-level
 * empty state would leave the ancestor requests firing — and every 403 they
 * got would look like a broken session.
 */
export function ActingClientGate({ children }: Props) {
  const { t } = useLocales();
  const { logout } = useAuthContext();
  // NAT-2804: logout itself ends in a full navigation to the login page; a router navigation
  // here would race it.
  const signOut = () => {
    logout().catch((error) => Sentry.captureException(error));
  };
  const acting = useActingClient();

  if (acting.role !== 'customer' || acting.status === 'ready') return <>{children}</>;

  const body = () => {
    if (acting.status === 'loading') return <LoadingScreen sx={{ minHeight: 240 }} />;
    if (acting.status === 'error') {
      return (
        <>
          <Typography variant="h4" sx={{ mb: 2 }}>
            {t('acting_client_error_title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 3 }}>
            {t('acting_client_error_description')}
          </Typography>
          <Button variant="contained" onClick={acting.refreshGrants}>
            {t('acting_client_retry')}
          </Button>
        </>
      );
    }
    if (acting.status === 'none') {
      return (
        <>
          <Typography variant="h4" sx={{ mb: 2 }}>
            {t('acting_client_none_title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            {t('acting_client_none_description')}
          </Typography>
        </>
      );
    }
    return (
      <>
        <Typography variant="h4" sx={{ mb: 1 }}>
          {t('acting_client_choose_title')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 4 }}>
          {t('acting_client_choose_description')}
        </Typography>
        <Stack spacing={1.5}>
          {acting.grants.map((grant) => (
            <Button
              key={grant.id}
              variant="outlined"
              size="large"
              onClick={() => acting.setActingClientId(String(grant.id))}
            >
              {grant.name || grant.slug}
            </Button>
          ))}
        </Stack>
      </>
    );
  };

  return (
    <Box sx={{ py: 10, px: 3, maxWidth: 560, mx: 'auto', textAlign: 'center' }}>
      {body()}
      {/* These states render without the header, so the account menu's sign-out
          is not reachable: a shared device must still be able to leave. */}
      <Button variant="text" color="inherit" onClick={signOut} sx={{ mt: 5 }}>
        {t('acting_client_logout')}
      </Button>
    </Box>
  );
}
